import { Dialog } from '@ordr-as/umami-react/dialog';
import { TextInput } from '@ordr-as/umami-react/form';
import { Radio, RadioGroup } from '@ordr-as/umami-react/radio';
import { useToast } from '@ordr-as/umami-react/toast';
import React, { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { OrderLineDto } from '../../../../../../../generated-api';
import Spinner from '../../../../../../components/Spinner';
import { useUser } from '../../../../../../context/AuthContext';
import { useLocation } from '../../../../../../context/LocationContext';
import { useScreenSize } from '../../../../../../context/ScreenSizeContext';
import useIntl from '../../../../../../hooks/useIntl';
import useSetNewCart from '../../../../../../hooks/useSetNewCart';
import { useCartStore } from '../../../../../../context/cart/CartStore';
import { useCreateWastages } from '@ordr-as/menu-builder-types/epos';

const getProductSizeId = (orderLine: OrderLineDto): string => {
  const variantOption = orderLine.options.find((option) => option.type === 'VARIANT');
  return variantOption?.itemVariantId ?? orderLine.itemVariantId;
};

enum REASONS {
  SPILLAGE = 'SPILLAGE',
  CUSTOMER_COMPLAINT = 'CUSTOMER_COMPLAINT',
  FAULTY_PRODUCT = 'FAULTY_PRODUCT',
  EXPIRED_PRODUCT = 'EXPIRED_PRODUCT',
  DRAFT_FOAM_LINE_CLEANING = 'DRAFT_FOAM_LINE_CLEANING',
  OTHER = 'OTHER',
}

type WastageDialogProps = {
  setOpenWasteDialog: Dispatch<SetStateAction<boolean>>;
  isOpen: boolean;
};

const WastageDialog = (props: WastageDialogProps) => {
  const { setOpenWasteDialog, isOpen } = props;
  const intl = useIntl();
  const [selectedReason, setSelectedReason] = useState<REASONS>();
  const [comment, setComment] = useState<string | undefined>();
  const { mutate: registerWastage, isPending } = useCreateWastages();
  const cart = useCartStore((x) => x.cart);
  const setNewCart = useSetNewCart();
  const toasts = useToast();
  const { isPhone, isWidescreen } = useScreenSize();
  const navigate = useNavigate();
  const { location } = useLocation();
  const user = useUser();

  const toastKey = '' + selectedReason;

  const handleSaveWastageReason = useCallback(() => {
    if (!selectedReason || !user?.email) {
      return;
    }

    const wastageData = cart.orderLines.map((orderLine) => ({
      quantity: orderLine.quantity,
      wastageType: selectedReason,
      comment: comment ?? null,
      productSizeId: getProductSizeId(orderLine),
      username: user.email,
    }));

    registerWastage(
      {
        locationId: location.id,
        data: wastageData,
      },
      {
        onSuccess: () => {
          setNewCart();
          if (!toasts.exists(toastKey)) {
            toasts.pushSuccess({
              id: toastKey,
              message: <span>{intl.formatMessage({ id: 'cart.wastage.dialog.success' })}</span>,
            });
          }
          setOpenWasteDialog(false);

          if (isPhone) {
            navigate(`/${location.locationId}`);
          }
        },
        onError: () => {
          if (!toasts.exists(toastKey)) {
            toasts.pushError({
              id: toastKey,
              message: <span>{intl.formatMessage({ id: 'cart.wastage.dialog.error' })}</span>,
            });
            setTimeout(() => {
              toasts.remove(toastKey);
            }, 3000);
          }
        },
      }
    );
  }, [
    cart,
    comment,
    navigate,
    intl,
    isPhone,
    location.id,
    location.locationId,
    registerWastage,
    selectedReason,
    setNewCart,
    setOpenWasteDialog,
    toastKey,
    toasts,
    user?.email,
  ]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <Dialog
        onClose={() => setOpenWasteDialog(false)}
        className="w-full mx-4 tablet:w-1/2 tablet:mx-0"
        title={intl.formatMessage({ id: `cart.wastage.dialog.title` })}
        actionLabel={intl.formatMessage({ id: 'generic.confirm' })}
        onAction={handleSaveWastageReason}
        isDisabledAction={isPending || !selectedReason}
        secondaryActionLabel={intl.formatMessage({ id: 'generic.cancel' })}
        onSecondaryAction={() => setOpenWasteDialog(false)}
        isDisabledSecondaryAction={isPending}
        size={isPhone ? 'fullscreen' : 'default'}
      >
        <div className={`${!isWidescreen && !isPhone && 'h-3/4-screen'}`}>
          {isPending && (
            <div className="flex items-center justify-center">
              <Spinner className="w-8 h-8" />
            </div>
          )}
          <RadioGroup
            defaultValue={selectedReason}
            aria-label="Select Option"
            onChange={(e) => setSelectedReason(e as REASONS)}
          >
            {Object.keys(REASONS).map((reason: string) => {
              return (
                <Radio key={reason} className="pb-2 mb-4" value={reason}>
                  <FormattedMessage id={`cart.wastage.dialog.reason.type.${reason}`} />
                </Radio>
              );
            })}
          </RadioGroup>
          <TextInput
            label={intl.formatMessage({ id: 'cart.wastage.dialog.reason.comment.title' })}
            labelClassName="text-grey-dark"
            multiline
            placeholder={intl.formatMessage({ id: 'cart.wastage.dialog.reason.comment.placeholder' })}
            onChange={(e) => setComment(e)}
          />
        </div>
      </Dialog>
    </>
  );
};

export default React.memo(WastageDialog);
